import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  ALL_PAGES,
  applyFilters,
  compareRows,
  EMPTY_FILTERS,
  hasAnyActiveFilter,
  isSharedWithMe,
  isVisibleToUser,
  MyReportsFilterState,
  MyReportsSortModel,
  isOwnedByMe,
} from './myReportsListHelpers';
import {
  buildPageToggleOptions,
  buildScopeToggleOptions,
  collectOwnerOptions,
  OptionItem,
} from './myReportsOptions';
import { MyReport } from './MyReportsTable.types';
import { QuickToggleOption } from '../../../components/molecules/QuickToggleGroup';
import { useIsClient } from '../../../hooks/useIsClient';
import { useGetSavedReports } from '../../../queries/report-query';
import { useGetLoggedInSiblingParentDetails } from '../../../queries/structure-query';
import { useDebouncedValue } from '../../../utils/use-debounced-value';

export interface MyReportsListDataReturn {
  myReports: MyReport[];
  sharedReports: MyReport[];
  otherReports: MyReport[];
  /** Reports currently on screen — shrinks as filters are applied. */
  total: number;
  /** Every report this user may see, ignoring filters. Drives the header badge. */
  totalVisible: number;
  isFetching: boolean;
  /** True until the first fetch settles — see the comment at its assignment. */
  isInitialLoad: boolean;
  isError: boolean;
  filters: MyReportsFilterState;
  setFilters: (filters: MyReportsFilterState) => void;
  resetFilters: () => void;
  /** True when the list is pinned to one page and the page toggles are hidden. */
  isPageLocked: boolean;
  /** Whether the filters that produced the current rows were actually filtering. */
  hasActiveFilters: boolean;
  sortModel: MyReportsSortModel;
  setSortModel: (sortModel: MyReportsSortModel) => void;
  pageToggleOptions: QuickToggleOption[];
  scopeToggleOptions: QuickToggleOption[];
  ownerOptions: OptionItem[];
  mySiblingId: number | undefined;
  refetch: () => void;
}

const INITIAL_QUERY_OPTIONS = {
  filterModel: { items: [] },
  page: 0,
  // Every row is fetched once and filtered in memory, which is what makes search
  // instant. Reports beyond this ceiling are not shown; if it is ever reached in
  // practice the answer is server-side search, not client-side paging.
  pageSize: 500,
  sortModel: [],
};

/**
 * Filtering is in-memory, so the cost of a keystroke is not the match — it is
 * re-rendering up to 500 rows. Short enough to feel instant, long enough that a
 * fast typist renders once instead of per character.
 */
const QUICK_FIND_DEBOUNCE_MS = 150;

export interface UseMyReportsListDataOptions {
  /**
   * Pins the list to one report type (e.g. 'ServiceList') and hides the page
   * toggles. Used when the list is embedded in a page's own Reports modal,
   * where offering other pages' reports would be a dead end.
   */
  lockedPageName?: string;
}

export const useMyReportsListData = ({
  lockedPageName,
}: UseMyReportsListDataOptions = {}): MyReportsListDataReturn => {
  const savedReportsQuery = useGetSavedReports(INITIAL_QUERY_OPTIONS);
  const siblingQuery = useGetLoggedInSiblingParentDetails();
  const isClient = useIsClient();

  const [filters, setFilters] = useState<MyReportsFilterState>(() =>
    lockedPageName ? { ...EMPTY_FILTERS, pageName: lockedPageName } : EMPTY_FILTERS
  );
  const [sortModel, setSortModel] = useState<MyReportsSortModel>({
    field: 'name',
    direction: 'asc',
  });

  // Belt-and-braces for a host that swaps the locked page while mounted. The
  // guard makes it a no-op in the normal case, so it can't stomp on the user's
  // other filters.
  useEffect(() => {
    if (!lockedPageName) return;
    setFilters((prev) =>
      prev.pageName === lockedPageName ? prev : { ...prev, pageName: lockedPageName }
    );
  }, [lockedPageName]);

  useEffect(() => {
    savedReportsQuery.refetch();
    siblingQuery.refetch();
    // Mount-only: refetch both queries once on entry. The query objects change
    // identity on every render, so listing them as deps would re-fire the
    // refetches in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allRows = useMemo<MyReport[]>(
    () => savedReportsQuery.data?.data ?? [],
    [savedReportsQuery.data]
  );
  // `siblingDetails` is typed as required, so this only bites on a contract
  // deviation — but an unguarded second hop would take the whole page down.
  const mySiblingId = siblingQuery.data?.siblingDetails?.id;

  // One decision about what this user may see, reused by the options, the
  // grouping and both totals, so they can't drift apart.
  const visibleRows = useMemo(
    () => allRows.filter((row) => isVisibleToUser(row, mySiblingId, isClient)),
    [allRows, mySiblingId, isClient]
  );

  // Only the free-text leg is debounced; ticking a toggle applies at once.
  const debouncedName = useDebouncedValue(filters.name, QUICK_FIND_DEBOUNCE_MS);
  // Keyed on the individual fields rather than `filters`: that object is
  // replaced on every keystroke, so depending on it would recompute the whole
  // filter/count/sort chain per character — exactly what the debounce exists to
  // avoid. The unchanged fields keep their references across a name edit.
  const activeFilters = useMemo<MyReportsFilterState>(
    () => ({
      name: debouncedName,
      pageName: filters.pageName,
      ownerIds: filters.ownerIds,
      scope: filters.scope,
    }),
    [debouncedName, filters.pageName, filters.ownerIds, filters.scope]
  );

  const filterOptions = useMemo(
    () => ({ includeInternalFields: !isClient, mySiblingId }),
    [isClient, mySiblingId]
  );

  // Each toggle group is counted over the rows passing every filter but its own,
  // so a count answers "how many if I click this" rather than "how many now" —
  // which for the unselected options would always be zero.
  const pageCountRows = useMemo(
    () => applyFilters(visibleRows, activeFilters, { ...filterOptions, only: { page: false } }),
    [visibleRows, activeFilters, filterOptions]
  );
  const scopeCountRows = useMemo(
    () => applyFilters(visibleRows, activeFilters, { ...filterOptions, only: { scope: false } }),
    [visibleRows, activeFilters, filterOptions]
  );
  const ownerOptionRows = useMemo(
    () => applyFilters(visibleRows, activeFilters, { ...filterOptions, only: { owner: false } }),
    [visibleRows, activeFilters, filterOptions]
  );

  const pageToggleOptions = useMemo(
    () => buildPageToggleOptions(visibleRows, pageCountRows),
    [visibleRows, pageCountRows]
  );
  const scopeToggleOptions = useMemo(
    () => buildScopeToggleOptions(scopeCountRows, mySiblingId, isClient),
    [scopeCountRows, mySiblingId, isClient]
  );
  // Counted without the owner filter, so selecting one owner doesn't drop the
  // others from the dropdown and strand the user on their own choice. The name,
  // page and group filters do still narrow it — those are the rows on screen.
  const ownerOptions = useMemo(() => collectOwnerOptions(ownerOptionRows), [ownerOptionRows]);

  const filteredRows = useMemo(
    () => applyFilters(visibleRows, activeFilters, filterOptions),
    [visibleRows, activeFilters, filterOptions]
  );

  const sortedRows = useMemo(
    () => [...filteredRows].sort((a, b) => compareRows(a, b, sortModel)),
    [filteredRows, sortModel]
  );

  const { myReports, sharedReports, otherReports } = useMemo(() => {
    const mine: MyReport[] = [];
    const shared: MyReport[] = [];
    const other: MyReport[] = [];

    for (const row of sortedRows) {
      if (isOwnedByMe(row, mySiblingId)) mine.push(row);
      else if (isSharedWithMe(row, mySiblingId)) shared.push(row);
      // Unreachable for clients: isVisibleToUser already withheld anything that
      // would land here, so the "Other" group is internal-only by construction.
      else other.push(row);
    }
    return { myReports: mine, sharedReports: shared, otherReports: other };
  }, [sortedRows, mySiblingId]);

  // The lock is enforced here rather than only hiding the toggles, so no code
  // path — reset included — can filter the embedded list off its own page.
  const setFiltersLocked = useCallback(
    (next: MyReportsFilterState) =>
      setFilters(lockedPageName ? { ...next, pageName: lockedPageName } : next),
    [lockedPageName]
  );

  const resetFilters = useCallback(
    () =>
      setFilters(lockedPageName ? { ...EMPTY_FILTERS, pageName: lockedPageName } : EMPTY_FILTERS),
    [lockedPageName]
  );

  const refetch = useCallback(() => {
    savedReportsQuery.refetch();
    siblingQuery.refetch();
  }, [savedReportsQuery, siblingQuery]);

  return {
    myReports,
    sharedReports,
    otherReports,
    total: sortedRows.length,
    totalVisible: visibleRows.length,
    isFetching: savedReportsQuery.isFetching || siblingQuery.isFetching,
    // Both queries are `enabled: false` and kicked off by the mount effect above,
    // so on first paint `isFetching` is still false and `data` is undefined —
    // which is exactly when the page used to flash its empty state. `isPending`
    // covers that window because it means "no data and no error yet",
    // independent of whether a fetch is in flight.
    isInitialLoad: savedReportsQuery.isPending || siblingQuery.isPending,
    isError: savedReportsQuery.isError || siblingQuery.isError,
    filters,
    setFilters: setFiltersLocked,
    resetFilters,
    isPageLocked: Boolean(lockedPageName),
    // A locked page isn't a filter the user chose, so it must not make the list
    // look filtered — otherwise an embedded list with no matches would claim
    // "no reports match your filters" for filters the user never set.
    hasActiveFilters: hasAnyActiveFilter(
      lockedPageName ? { ...activeFilters, pageName: ALL_PAGES } : activeFilters
    ),
    sortModel,
    setSortModel,
    pageToggleOptions,
    scopeToggleOptions,
    ownerOptions,
    mySiblingId,
    refetch,
  };
};
